import { extractCollectionItems, parseApiPayload } from "../apiResponse";
import config from "../../resources/service_config.json";
import { runtimeEnv } from "../runtimeEnv";
import { getTokenRoleSnapshot } from "./connectorRouting";

const STORAGE_KEY = "dsb.endpointRuntime";
const UPDATE_EVENT = "dsb:endpoint-runtime-updated";
const WORKER_PORT_REQUIRED_ENVIRONMENTS = new Set(["local", "development"]);

function safeJsonParse(value) {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function getSessionStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function getRuntimeBaseUrl() {
  const envKey = runtimeEnv.REACT_APP_ENV;

  if (envKey === "development") {
    return runtimeEnv.REACT_APP_URI_DEVELOP;
  }

  if (envKey === "test") {
    return runtimeEnv.REACT_APP_URI_TEST;
  }

  if (envKey === "pro") {
    return runtimeEnv.REACT_APP_URI_PRO || runtimeEnv.REACT_APP_URI_PROD;
  }

  if (envKey === "pre" || envKey === "preproduction") {
    return (
      runtimeEnv.REACT_APP_URI_PRE ||
      runtimeEnv.REACT_APP_URI_PREPRODUCTION ||
      runtimeEnv.REACT_APP_URI_PRO ||
      runtimeEnv.REACT_APP_URI_PROD
    );
  }

  if (envKey === "production") {
    return runtimeEnv.REACT_APP_URI_PROD;
  }

  if (envKey === "local") {
    return runtimeEnv.REACT_APP_URI_LOCAL;
  }

  return "";
}

function normalizePathPrefix(value) {
  const path = String(value || "").trim();

  if (!path || path === "/") {
    return "";
  }

  return path.startsWith("/") ? path : `/${path}`;
}

function joinUrlPath(...parts) {
  return parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .map((part, index) => {
      if (index === 0) {
        return part.replace(/\/+$/, "");
      }

      return part.replace(/^\/+|\/+$/g, "");
    })
    .join("/");
}

function getConnectorMasterPathPrefix() {
  const envKey = runtimeEnv.REACT_APP_ENV;
  const servicePathOrPort = config[envKey]?.services?.connectorMaster;

  if (!servicePathOrPort || /^:\d+(\/.*)?$/.test(servicePathOrPort)) {
    return "";
  }

  return normalizePathPrefix(servicePathOrPort);
}

function normalizePort(value) {
  const port = String(value ?? "")
    .trim()
    .replace(/^:/, "");

  return /^\d+$/.test(port) ? port : "";
}

function normalizeHost(value) {
  let host = String(value ?? "").trim();

  if (!host) {
    return "";
  }

  try {
    if (host.includes("://")) {
      return new URL(host).hostname;
    }

    return new URL(`http://${host}`).hostname;
  } catch {
    host = host.replace(/^[a-z]+:\/\//i, "");
    host = host.replace(/[/?#].*$/, "");

    if (!host.startsWith("[") && (host.match(/:/g) || []).length === 1) {
      return host.slice(0, host.lastIndexOf(":"));
    }

    return host;
  }
}

function normalizeConnectorUrl(value) {
  const connectorUrl = String(value ?? "").trim();

  if (!connectorUrl) {
    return "";
  }

  const hasProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(connectorUrl);
  const hasPath = connectorUrl.includes("/");

  if (!hasProtocol && !hasPath) {
    return "";
  }

  const baseProtocol = (() => {
    try {
      return new URL(getRuntimeBaseUrl()).protocol;
    } catch {
      return "https:";
    }
  })();
  const urlCandidate = hasProtocol
    ? connectorUrl
    : `${baseProtocol}//${connectorUrl}`;
  const sanitizedConnectorUrl = urlCandidate
    .replace(/[?#].*$/, "")
    .replace(/\/$/, "");

  try {
    new URL(sanitizedConnectorUrl);
    return sanitizedConnectorUrl;
  } catch {
    return sanitizedConnectorUrl;
  }
}

function shouldRequireWorkerPort() {
  return WORKER_PORT_REQUIRED_ENVIRONMENTS.has(
    String(runtimeEnv.REACT_APP_ENV ?? "")
      .trim()
      .toLowerCase(),
  );
}

function hasUsableConnectorAddress(candidate) {
  if (!candidate || typeof candidate !== "object") {
    return false;
  }

  const workerPort = normalizePort(candidate?.workerPort);
  const connectorUrl = normalizeConnectorUrl(candidate?.connectorHost);

  if (workerPort || connectorUrl) {
    return true;
  }

  if (shouldRequireWorkerPort()) {
    return false;
  }

  return Boolean(normalizeHost(candidate?.connectorHost));
}

function buildOriginFromRuntimeConfig({ host, port } = {}) {
  const baseUrl = getRuntimeBaseUrl();

  if (!baseUrl) {
    return "";
  }

  const base = new URL(baseUrl);
  const resolvedHost = normalizeHost(host) || base.hostname;
  const resolvedPort = normalizePort(port) || base.port;

  return `${base.protocol}//${resolvedHost}${resolvedPort ? `:${resolvedPort}` : ""}`;
}

function buildConnectorUrlFromSummaryItem(summaryItem) {
  const connectorUrl = normalizeConnectorUrl(summaryItem?.connectorHost);

  if (connectorUrl) {
    return connectorUrl;
  }

  return buildOriginFromRuntimeConfig({
    host: normalizeHost(summaryItem?.connectorHost),
    port: summaryItem?.workerPort,
  });
}

function normalizeRouteConfig(routeConfig) {
  const connectorUrl = normalizeConnectorUrl(
    routeConfig?.connectorUrl ?? routeConfig?.url ?? routeConfig?.connectorHost,
  );

  return {
    port: normalizePort(routeConfig?.port ?? routeConfig?.workerPort),
    host: normalizeHost(routeConfig?.host ?? routeConfig?.connectorHost),
    connectorUrl,
  };
}

function hasUsableRuntimeConfig(config) {
  return Boolean(
    normalizePort(config?.port) ||
      normalizeConnectorUrl(config?.connectorUrl) ||
      (!shouldRequireWorkerPort() && normalizeHost(config?.host)),
  );
}

function buildRoleRoutesFromPayload(payload) {
  const routes = {};

  for (const candidate of getPayloadCandidates(payload)) {
    const role = getSummaryRole(candidate);

    if (!role || routes[role] || !hasUsableConnectorAddress(candidate)) {
      continue;
    }

    routes[role] = normalizeRouteConfig(candidate);
  }

  return routes;
}

function emitRuntimeUpdate(detail) {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(UPDATE_EVENT, {
      detail,
    }),
  );
}

function readStoredConfig() {
  if (typeof window !== "undefined" && window.__DSB_ENDPOINT_RUNTIME__) {
    return window.__DSB_ENDPOINT_RUNTIME__;
  }

  const rawConfig = getSessionStorage()?.getItem(STORAGE_KEY);

  if (!rawConfig) {
    return null;
  }

  const parsedConfig = safeJsonParse(rawConfig);
  return parsedConfig && typeof parsedConfig === "object" ? parsedConfig : null;
}

function getPayloadCandidates(payload) {
  const parsedPayload = parseApiPayload(payload);
  const collectionItems = extractCollectionItems(parsedPayload);
  const containers = [
    ...collectionItems,
    parsedPayload,
    parsedPayload?.result,
    parsedPayload?.data,
    parsedPayload?.content,
    parsedPayload?.items,
  ];

  return containers
    .flatMap((container) => (Array.isArray(container) ? container : [container]))
    .filter((candidate) => candidate && typeof candidate === "object");
}

function getSummaryRole(candidate) {
  return String(candidate?.role ?? candidate?.ssoo ?? candidate?.sso ?? "")
    .trim()
    .toLowerCase();
}

function getBootstrapSummaryRole(snapshot) {
  if (snapshot?.isAdmin) {
    return "admin";
  }

  if (snapshot?.hasProviderRole) {
    return "data_provider";
  }

  if (snapshot?.isConsumerOnly) {
    return "data_consumer";
  }

  return "";
}

function normalizeSummaryRole(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function selectSummaryItemFromPayload(payload, preferredRole = "") {
  const candidates = getPayloadCandidates(payload);
  const usableCandidates = candidates.filter(hasUsableConnectorAddress);
  const normalizedPreferredRole = normalizeSummaryRole(preferredRole);

  if (normalizedPreferredRole) {
    const roleMatch = usableCandidates.find(
      (candidate) => getSummaryRole(candidate) === normalizedPreferredRole,
    );

    if (roleMatch) {
      return roleMatch;
    }

    const hasRoleTaggedCandidates = candidates.some((candidate) =>
      getSummaryRole(candidate),
    );

    return hasRoleTaggedCandidates ? null : usableCandidates[0] ?? null;
  }

  return usableCandidates[0] ?? null;
}

function getStoredConfigSummaryRole(config) {
  return normalizeSummaryRole(
    config?.summaryRole ?? config?.role ?? config?.ssoo,
  );
}

function isValidStoredConfig(config) {
  const hasValidRoleRoute = Object.values(config?.routes ?? {}).some(
    hasUsableRuntimeConfig,
  );

  return Boolean(hasUsableRuntimeConfig(config) || hasValidRoleRoute);
}

export function getAdminConnectorUpdateEventName() {
  return UPDATE_EVENT;
}

export function getConfiguredAdminPort() {
  return normalizePort(
    runtimeEnv.REACT_ADMIN_PORT || runtimeEnv.REACT_APP_ADMIN_PORT,
  );
}

export function getDecodedParticipantId(decoded) {
  const directParticipantId = Array.isArray(decoded?.participant_id)
    ? decoded.participant_id[0]
    : decoded?.participant_id;

  const attributeParticipantId = Array.isArray(decoded?.attributes?.participant_id)
    ? decoded.attributes.participant_id[0]
    : decoded?.attributes?.participant_id;

  return String(directParticipantId || attributeParticipantId || "").trim();
}

export function getStoredAdminConnectorConfig() {
  return readStoredConfig();
}

export function getResolvedAdminWorkerPort() {
  return normalizePort(readStoredConfig()?.port);
}

export function getResolvedAdminConnectorHost() {
  const config = readStoredConfig();
  return normalizeHost(config?.host ?? config?.connectorHost);
}

export function getResolvedAdminConnectorUrl() {
  return normalizeConnectorUrl(readStoredConfig()?.connectorUrl);
}

export function clearStoredAdminConnectorConfig() {
  if (typeof window !== "undefined") {
    delete window.__DSB_ENDPOINT_RUNTIME__;
  }

  getSessionStorage()?.removeItem(STORAGE_KEY);
  emitRuntimeUpdate(null);
}

export function setStoredAdminConnectorConfig(config) {
  const connectorUrl = normalizeConnectorUrl(
    config?.connectorUrl ?? config?.url ?? config?.connectorHost,
  );
  const normalizedRoutes = Object.entries(config?.routes ?? {}).reduce(
    (routes, [role, routeConfig]) => {
      const normalizedRole = normalizeSummaryRole(role);
      const normalizedRoute = normalizeRouteConfig(routeConfig);

      if (normalizedRole && hasUsableRuntimeConfig(normalizedRoute)) {
        routes[normalizedRole] = normalizedRoute;
      }

      return routes;
    },
    {},
  );
  const normalizedConfig = {
    ...config,
    role: String(config?.role ?? "").trim(),
    summaryRole: normalizeSummaryRole(
      config?.summaryRole ?? config?.role ?? config?.ssoo,
    ),
    participantId: String(config?.participantId ?? "").trim(),
    port: normalizePort(config?.port ?? config?.workerPort),
    host: normalizeHost(config?.host ?? config?.connectorHost),
    connectorUrl,
    routes: normalizedRoutes,
  };

  if (!isValidStoredConfig(normalizedConfig)) {
    clearStoredAdminConnectorConfig();
    return null;
  }

  if (typeof window !== "undefined") {
    window.__DSB_ENDPOINT_RUNTIME__ = normalizedConfig;
  }

  getSessionStorage()?.setItem(STORAGE_KEY, JSON.stringify(normalizedConfig));
  emitRuntimeUpdate(normalizedConfig);
  return normalizedConfig;
}

export function buildAdminConnectorRegistryUrl(participantId) {
  const baseUrl = getRuntimeBaseUrl();
  const adminPort = getConfiguredAdminPort();
  const connectorMasterPathPrefix = getConnectorMasterPathPrefix();
  const normalizedParticipantId = String(participantId ?? "").trim();

  if (!baseUrl || !normalizedParticipantId) {
    return "";
  }

  const url = new URL(baseUrl);
  if (connectorMasterPathPrefix) {
    url.pathname = joinUrlPath(
      url.pathname,
      connectorMasterPathPrefix,
      `/registry/connector-data/${encodeURIComponent(
        normalizedParticipantId,
      )}/summary`,
    );
  } else {
    if (!adminPort) {
      return "";
    }

    url.port = adminPort;
    url.pathname = `/registry/connector-data/${encodeURIComponent(
      normalizedParticipantId,
    )}/summary`;
  }
  url.search = "";
  url.hash = "";

  return url.toString();
}

export async function resolveParticipantConnectorOrigin(participantId, token) {
  const registryUrl = buildAdminConnectorRegistryUrl(participantId);

  if (!registryUrl) {
    throw new Error(
      "Missing runtime admin connector URL configuration. Check REACT_ADMIN_PORT.",
    );
  }

  const response = await fetch(registryUrl, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const rawResponse = await response.text();
  const payload = safeJsonParse(rawResponse);

  if (!response.ok) {
    throw new Error(
      `Participant connector origin lookup failed with status ${response.status}.`,
    );
  }

  const selectedSummaryItem = selectSummaryItemFromPayload(
    payload,
    "data_provider",
  );

  const workerPort = normalizePort(selectedSummaryItem?.workerPort);
  const connectorUrl = normalizeConnectorUrl(selectedSummaryItem?.connectorHost);
  const connectorHost = normalizeHost(selectedSummaryItem?.connectorHost);

  if (!selectedSummaryItem || (!workerPort && !connectorUrl && !connectorHost)) {
    throw new Error(
      "Participant connector origin lookup did not return a usable connector address.",
    );
  }

  if (!workerPort && !connectorUrl && shouldRequireWorkerPort()) {
    throw new Error(
      "Participant connector origin lookup did not return a workerPort value.",
    );
  }

  return buildConnectorUrlFromSummaryItem(selectedSummaryItem);
}

export async function bootstrapAdminConnectorRouting(token, options = {}) {
  const snapshot = getTokenRoleSnapshot(token);
  const participantId = String(
    options?.participantId ?? getDecodedParticipantId(snapshot.decoded),
  ).trim();

  if (!token) {
    clearStoredAdminConnectorConfig();
    return {
      status: "skipped",
      reason: "missing-token",
    };
  }

  if (snapshot?.isAdmin) {
    const adminPort = getConfiguredAdminPort();

    if (!adminPort) {
      throw new Error(
        "Missing runtime admin connector URL configuration. Check REACT_ADMIN_PORT.",
      );
    }

    const config = setStoredAdminConnectorConfig({
      role: "admin",
      participantId,
      summaryRole: "admin",
      port: adminPort,
      source: "env",
      resolvedAt: Date.now(),
    });

    return {
      status: "resolved",
      config,
    };
  }

  if (!participantId) {
    return {
      status: "skipped",
      reason: "missing-participant-id",
    };
  }

  const preferredSummaryRole = getBootstrapSummaryRole(snapshot);
  const cachedConfig = readStoredConfig();
  if (
    cachedConfig?.role !== "admin" &&
    cachedConfig?.participantId === participantId &&
    getStoredConfigSummaryRole(cachedConfig) === preferredSummaryRole &&
    isValidStoredConfig(cachedConfig)
  ) {
    return {
      status: "cached",
      config: cachedConfig,
    };
  }

  const registryUrl = buildAdminConnectorRegistryUrl(participantId);

  if (!registryUrl) {
    throw new Error(
      "Missing runtime admin connector URL configuration. Check REACT_ADMIN_PORT.",
    );
  }

  const response = await fetch(registryUrl, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const rawResponse = await response.text();
  const payload = safeJsonParse(rawResponse);

  if (!response.ok) {
    throw new Error(
      `Endpoint bootstrap failed with status ${response.status}.`,
    );
  }

  const selectedSummaryItem = selectSummaryItemFromPayload(
    payload,
    preferredSummaryRole,
  );
  const selectedSummaryRole = getSummaryRole(selectedSummaryItem);
  const workerPort = normalizePort(selectedSummaryItem?.workerPort);
  const connectorHost = normalizeHost(selectedSummaryItem?.connectorHost);
  const connectorUrl = normalizeConnectorUrl(selectedSummaryItem?.connectorHost);

  if (!selectedSummaryItem || (!workerPort && !connectorUrl && !connectorHost)) {
    throw new Error(
      preferredSummaryRole
        ? `Endpoint bootstrap did not return a usable connector address for ${preferredSummaryRole}.`
        : "Endpoint bootstrap did not return a usable connector address.",
    );
  }

  if (!workerPort && !connectorUrl && shouldRequireWorkerPort()) {
    throw new Error(
      preferredSummaryRole
        ? `Endpoint bootstrap did not return a workerPort value for ${preferredSummaryRole}.`
        : "Endpoint bootstrap did not return a workerPort value.",
    );
  }

  const config = setStoredAdminConnectorConfig({
    role: snapshot?.isAdmin ? "admin" : "user",
    summaryRole: selectedSummaryRole || preferredSummaryRole,
    participantId,
    port: workerPort,
    host: connectorHost,
    connectorUrl,
    routes: buildRoleRoutesFromPayload(payload),
    source: "connector-data",
    resolvedAt: Date.now(),
  });

  return {
    status: "resolved",
    config,
    payload,
  };
}
